import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../services/api';
import GsecLetterPrintButton from './GsecLetterPrintButton';
import DealConfirmationButton from './DealConfirmationButton';
import { isGsecFinalApproved } from '../utils/gsecLetterAccess';
import { getDealFilterDateYmd, toYmd } from '../utils/recentDealsDate';

const actionButtonStyle = { padding: '6px 14px', borderRadius: 4, border: 'none', cursor: 'pointer', fontSize: 13, lineHeight: 1.4 };

const pagerButtonStyle = (disabled) => ({
  padding: '6px 14px',
  borderRadius: 4,
  border: '1px solid #ccc',
  background: disabled ? '#f1f3f7' : '#fff',
  color: disabled ? '#999' : '#333',
  cursor: disabled ? 'default' : 'pointer',
  fontSize: 13,
  lineHeight: 1.4
});

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('en-GB');
  } catch {
    return dateStr;
  }
};

const formatCurrency = (value) => {
  if (!value && value !== 0) return '';
  return Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/**
 * Lists final_approved GSEC deals with settlement letter print (back office blotters).
 * When a value date is selected, loads ALL final-approved deals for that date (no 100-row cap).
 */
const GsecFinalApprovedLettersPanel = ({
  transactions,
  onView,
  pageSize = 50,
  maxItems = 100,
  valueDateYmd = null
}) => {
  const [page, setPage] = useState(1);
  const [valueDateFilter, setValueDateFilter] = useState(() => toYmd(valueDateYmd) || '');
  const [datedDeals, setDatedDeals] = useState(null);
  const [loadingDated, setLoadingDated] = useState(false);
  const [datedError, setDatedError] = useState('');

  useEffect(() => {
    const next = toYmd(valueDateYmd);
    if (next) setValueDateFilter(next);
  }, [valueDateYmd]);

  useEffect(() => {
    if (!valueDateFilter) {
      setDatedDeals(null);
      setDatedError('');
      return undefined;
    }

    let cancelled = false;
    (async () => {
      setLoadingDated(true);
      setDatedError('');
      try {
        const res = await axios.get(`${API_BASE_URL}/isin-master/gsec/final-approved`, {
          params: { valueDate: valueDateFilter }
        });
        if (!cancelled) {
          setDatedDeals(Array.isArray(res.data?.data) ? res.data.data : []);
          setPage(1);
        }
      } catch (err) {
        if (!cancelled) {
          setDatedDeals([]);
          setDatedError(err.response?.data?.message || err.message || 'Failed to load deals for value date');
        }
      } finally {
        if (!cancelled) setLoadingDated(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [valueDateFilter]);

  const finalApproved = useMemo(() => {
    // Date filter: uncapped list from API
    if (valueDateFilter && datedDeals != null) {
      return datedDeals.filter(isGsecFinalApproved);
    }
    // No date: fall back to parent list, still capped for performance
    const fromParent = (transactions || []).filter(isGsecFinalApproved);
    if (!valueDateFilter) {
      return fromParent.slice(0, maxItems);
    }
    return fromParent.filter((tx) => getDealFilterDateYmd(tx) === valueDateFilter);
  }, [transactions, maxItems, valueDateFilter, datedDeals]);

  const total = finalApproved.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize) || 1);
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * pageSize;
  const pageItems = finalApproved.slice(startIndex, startIndex + pageSize);

  return (
    <div style={{ marginTop: 32 }}>
      <h3 style={{ fontSize: '1.25rem', marginBottom: 8 }}>Final Approved — Settlement Letters</h3>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 12,
          alignItems: 'center',
          marginBottom: 12
        }}
      >
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#333' }}>
          Value date
          <input
            type="date"
            value={valueDateFilter}
            onChange={(e) => {
              setValueDateFilter(e.target.value || '');
              setPage(1);
            }}
            style={{ padding: '6px 10px', borderRadius: 4, border: '1px solid #ccc' }}
          />
        </label>
        {valueDateFilter && (
          <button
            type="button"
            onClick={() => {
              setValueDateFilter('');
              setDatedDeals(null);
              setPage(1);
            }}
            style={{ ...actionButtonStyle, background: '#6c757d', color: '#fff' }}
          >
            Clear date
          </button>
        )}
        {loadingDated && <span style={{ color: '#666', fontSize: 13 }}>Loading…</span>}
      </div>

      {datedError && (
        <p style={{ color: '#c00', marginTop: 0, marginBottom: 12 }}>{datedError}</p>
      )}

      {!total && !loadingDated ? (
        <p style={{ color: '#666', margin: 0 }}>
          {valueDateFilter
            ? `No final-approved deals with value date ${formatDate(valueDateFilter)}.`
            : 'No final-approved deals available for letter printing.'}
        </p>
      ) : (
        <>
          <p style={{ color: '#666', fontSize: 14, marginBottom: 12 }}>
            {valueDateFilter
              ? `All final-approved deals valued on ${formatDate(valueDateFilter)} (${total} deal${total === 1 ? '' : 's'}, no row limit).`
              : `Instruction letters can be printed only after full sign-off (showing up to ${maxItems} of recent deals${total ? `: ${total}` : ''}).`}
            {totalPages > 1 &&
              ` Showing ${startIndex + 1}–${startIndex + pageItems.length} on page ${currentPage} of ${totalPages}.`}
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ background: '#f1f3f7' }}>
                <tr>
                  <th style={{ padding: '8px', border: '1px solid #eee', textAlign: 'left' }}>Deal Number</th>
                  <th style={{ padding: '8px', border: '1px solid #eee', textAlign: 'left' }}>Value Date</th>
                  <th style={{ padding: '8px', border: '1px solid #eee', textAlign: 'left' }}>Buy/Sell</th>
                  <th style={{ padding: '8px', border: '1px solid #eee', textAlign: 'left' }}>ISIN</th>
                  <th style={{ padding: '8px', border: '1px solid #eee', textAlign: 'right' }}>Settlement</th>
                  <th style={{ padding: '8px', border: '1px solid #eee', textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map((tx) => (
                  <tr key={tx.id}>
                    <td style={{ padding: '8px', border: '1px solid #eee' }}>{tx.deal_number || tx.dealNumber}</td>
                    <td style={{ padding: '8px', border: '1px solid #eee' }}>
                      {formatDate(tx.value_date || tx.valueDate)}
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #eee', fontWeight: 'bold' }}>
                      {tx.transaction_type || tx.transactionType || 'N/A'}
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #eee' }}>{tx.isin || tx.isin_number || '—'}</td>
                    <td style={{ padding: '8px', border: '1px solid #eee', textAlign: 'right' }}>
                      {formatCurrency(tx.settlement_amount || tx.settlementAmount)}
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #eee', textAlign: 'center' }}>
                      <div
                        style={{
                          display: 'flex',
                          gap: 8,
                          justifyContent: 'center',
                          alignItems: 'center',
                          flexWrap: 'wrap'
                        }}
                      >
                        {onView && (
                          <button
                            type="button"
                            onClick={() => onView(tx)}
                            style={{ ...actionButtonStyle, background: '#007bff', color: '#fff' }}
                          >
                            View
                          </button>
                        )}
                        <GsecLetterPrintButton
                          transaction={tx}
                          style={{ ...actionButtonStyle, background: '#0d6efd', color: '#fff' }}
                          className=""
                          label="🖨 Letter"
                        />
                        <DealConfirmationButton
                          dealType="gsec"
                          dealId={tx.id}
                          refNumberHint={tx.deal_number || tx.dealNumber}
                          className=""
                          style={{ ...actionButtonStyle, background: '#6c757d', color: '#fff' }}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: 12,
                marginTop: 12
              }}
            >
              <button
                type="button"
                onClick={() => setPage(currentPage - 1)}
                disabled={currentPage <= 1}
                style={pagerButtonStyle(currentPage <= 1)}
              >
                Previous
              </button>
              <span style={{ color: '#666', fontSize: 14 }}>
                Page {currentPage} of {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage(currentPage + 1)}
                disabled={currentPage >= totalPages}
                style={pagerButtonStyle(currentPage >= totalPages)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default GsecFinalApprovedLettersPanel;
